// src/components/category_page/FilterPanel.js
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useProductData } from "../../context/ProductDataContext";
import {
  activeFacetCount,
  asArray,
  facetViewModels,
  flattenCategoryTree,
  formatAttributeValue,
  groupFacetViews,
  optionLabel,
} from "../../lib/productAttributes";

const ALL_VALUE = "";

export default function FilterPanel({
  categoryId,
  activeFilters,
  setActiveFilters,
  categories = [],
  onSelectCategory,
}) {
  const {
    products,
    attributeConfig,
    attributeProfiles,
    schemaLabels,
  } = useProductData();

  const selectedCategoryId = Array.isArray(activeFilters?.categoryIds)
    ? activeFilters.categoryIds[0] || ALL_VALUE
    : categoryId || ALL_VALUE;

  const categoryOptions = useMemo(
    () => flattenCategoryTree(categories).filter((category) => !(category.children || []).length),
    [categories]
  );

  const facets = useMemo(
    () =>
      facetViewModels({
        products,
        categoryId: selectedCategoryId || null,
        categories,
        profileConfig: attributeProfiles,
        attributeConfig,
        schemaLabels,
        activeFilters,
      }),
    [products, selectedCategoryId, categories, attributeProfiles, attributeConfig, schemaLabels, activeFilters]
  );

  const groups = useMemo(() => groupFacetViews(facets, attributeConfig), [facets, attributeConfig]);
  const selectedCount = activeFacetCount(activeFilters);

  const setFacet = (facetId, value) => {
    setActiveFilters?.((previous) => {
      const nextFacets = { ...(previous?.facets || {}) };
      const emptyArray = Array.isArray(value) && value.length === 0;
      const emptyRange = value && typeof value === "object" && !Array.isArray(value)
        && (value.min === "" || value.min === undefined)
        && (value.max === "" || value.max === undefined);
      if (value === undefined || value === null || value === "" || emptyArray || emptyRange) {
        delete nextFacets[facetId];
      } else {
        nextFacets[facetId] = value;
      }
      return { ...(previous || {}), facets: nextFacets };
    });
  };

  const toggleTerm = (facetId, value, checked) => {
    const current = new Set(asArray(activeFilters?.facets?.[facetId]).map(String));
    checked ? current.add(String(value)) : current.delete(String(value));
    setFacet(facetId, Array.from(current));
  };

  const clearFacets = () => {
    setActiveFilters?.((previous) => ({ ...(previous || {}), facets: {} }));
  };

  const selectedChips = facets.flatMap((facet) => {
    if (facet.type === "terms" || facet.type === "single") {
      return asArray(activeFilters?.facets?.[facet.id]).map((value) => ({
        key: `${facet.id}:${value}`,
        label: `${facet.attribute.label}: ${optionLabel(facet.attribute, value, schemaLabels)}`,
        remove: () => toggleTerm(facet.id, value, false),
      }));
    }
    if (facet.type === "boolean" && typeof activeFilters?.facets?.[facet.id] === "boolean") {
      const selected = activeFilters.facets[facet.id];
      const valueLabel = selected
        ? facet.attribute.trueLabel || "Igen"
        : facet.attribute.falseLabel || "Nem";
      return [{
        key: facet.id,
        label: `${facet.attribute.label}: ${valueLabel}`,
        remove: () => setFacet(facet.id, undefined),
      }];
    }
    if (facet.type === "range" && activeFilters?.facets?.[facet.id]) {
      const selected = activeFilters.facets[facet.id];
      const text = formatAttributeValue(
        { min: selected.min === "" ? undefined : selected.min, max: selected.max === "" ? undefined : selected.max },
        { ...facet.attribute, valueType: "range" },
        schemaLabels
      );
      return text
        ? [{ key: facet.id, label: `${facet.attribute.label}: ${text}`, remove: () => setFacet(facet.id, undefined) }]
        : [];
    }
    return [];
  });

  return (
    <div className="space-y-5">
      {!categoryId && categoryOptions.length > 0 && (
        <div>
          <label className="block font-semibold text-sm mb-2">Kategória</label>
          <select
            className="w-full border rounded-lg px-3 py-2 bg-white"
            value={selectedCategoryId}
            onChange={(event) => {
              const nextId = event.target.value;
              setActiveFilters?.((previous) => ({ ...(previous || {}), categoryIds: nextId ? [nextId] : [] }));
              if (nextId) onSelectCategory?.(nextId);
            }}
          >
            <option value={ALL_VALUE}>Összes termék</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>{category.name || category.id}</option>
            ))}
          </select>
        </div>
      )}

      {selectedChips.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-semibold text-sm">Aktív szűrők ({selectedCount})</span>
            <button type="button" className="text-xs text-blue-700 hover:underline" onClick={clearFacets}>
              Összes törlése
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.remove}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs text-blue-900 hover:bg-blue-100"
                title="Szűrő eltávolítása"
              >
                {chip.label}<span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {groups.map((group) => (
        <details key={group.id} defaultOpen={group.defaultOpen} className="border-t pt-4 first:border-t-0 first:pt-0">
          <summary className="cursor-pointer select-none font-bold text-gray-900 mb-3">{group.label}</summary>
          <div className="space-y-5 pt-2">
            {group.facets.map((facet) => {
              const current = activeFilters?.facets?.[facet.id];

              if (facet.type === "boolean") {
                const currentValue = typeof current === "boolean" ? String(current) : "";
                const trueLabel = facet.attribute.trueLabel || "Igen";
                const falseLabel = facet.attribute.falseLabel || "Nem";
                return (
                  <div key={facet.id}>
                    <label className="block font-medium text-sm mb-2" htmlFor={`facet-${facet.id}`}>
                      {facet.attribute.label}
                    </label>
                    <select
                      id={`facet-${facet.id}`}
                      className="w-full border rounded-lg px-3 py-2 bg-white text-sm"
                      value={currentValue}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFacet(facet.id, value === "" ? undefined : value === "true");
                      }}
                    >
                      <option value="">Mindegy</option>
                      <option value="true">{trueLabel} ({facet.trueCount})</option>
                      <option value="false">{falseLabel} ({facet.falseCount})</option>
                    </select>
                  </div>
                );
              }

              if (facet.type === "range") {
                return (
                  <div key={facet.id}>
                    <label className="block font-medium text-sm mb-2">
                      {facet.attribute.label}
                      {facet.attribute.unit ? <span className="text-gray-500"> ({facet.attribute.unit})</span> : null}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={current?.min ?? ""}
                        min={facet.min}
                        max={facet.max}
                        placeholder={String(facet.min)}
                        onChange={(event) => setFacet(facet.id, { min: event.target.value, max: current?.max ?? "" })}
                        aria-label={`${facet.attribute.label} minimum`}
                      />
                      <input
                        type="number"
                        className="w-full border rounded-lg px-2 py-1.5 text-sm"
                        value={current?.max ?? ""}
                        min={facet.min}
                        max={facet.max}
                        placeholder={String(facet.max)}
                        onChange={(event) => setFacet(facet.id, { min: current?.min ?? "", max: event.target.value })}
                        aria-label={`${facet.attribute.label} maximum`}
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div key={facet.id}>
                  <div className="font-medium text-sm mb-2">{facet.attribute.label}</div>
                  <div className="space-y-1.5 max-h-52 overflow-auto pr-1">
                    {facet.options.map((option) => {
                      const checked = asArray(current).map(String).includes(option.value);
                      return (
                        <label key={option.value} className="flex items-center justify-between gap-2 cursor-pointer text-sm">
                          <span className="flex items-center gap-2 min-w-0">
                            <input
                              type={facet.type === "single" ? "radio" : "checkbox"}
                              name={facet.type === "single" ? facet.id : undefined}
                              checked={checked}
                              onChange={(event) =>
                                facet.type === "single"
                                  ? setFacet(facet.id, event.target.checked ? [option.value] : [])
                                  : toggleTerm(facet.id, option.value, event.target.checked)
                              }
                            />
                            <span className="truncate">{option.label}</span>
                          </span>
                          <span className="text-xs text-gray-500">{option.count}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      ))}

      {!groups.length && (
        <p className="text-sm text-gray-500">Ehhez a termékkörhöz jelenleg nincs további hasznos szűrő.</p>
      )}
    </div>
  );
}

FilterPanel.propTypes = {
  categoryId: PropTypes.string,
  activeFilters: PropTypes.object,
  setActiveFilters: PropTypes.func,
  categories: PropTypes.array,
  onSelectCategory: PropTypes.func,
};
